import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { GeneratedDocumentTypeSchema } from './deal-documents.schemas';
import { SigningFormSchema } from './signing-page.schemas';

export const SIGNING_DECLARATION_KEYS = [
  'IDENTITY',
  'NOTICE',
  'ACCEPTANCE',
  'EARLY_START',
  'EARLY_START_INFO',
  'COMMISSION',
  'SUBMIT',
  'PRIVACY',
] as const;
export const SIGNING_EMAIL_KINDS = [
  'INVITATION',
  'REMINDER',
  'CONFIRMATION',
  'COMPLETION',
  'ACCEPTANCE_REMINDER',
  'WITHDRAWAL_RECEIPT',
] as const;

// The stored snapshot `content` exactly as IMM-1171.3 wrote it. A separate file: `signing-page.schemas` →
// `deal-documents.schemas` → `deal-signing-requests.schemas` is an import chain, so the summary file cannot import it.
export const SigningPageReplaySchema = z.object({
  textsVersion: z.string(),
  identity: z.string(),
  privacy: z.string(),
  form: SigningFormSchema,
  document: z.object({ fileName: z.string(), pageCount: z.number().int().min(1), sentFingerprint: z.string() }),
});

export const SigningDeclarationSchema = z.object({
  key: z.enum(SIGNING_DECLARATION_KEYS),
  text: z.string(), // word for word from the snapshot the signature was recorded against
  checkbox: z.enum(['CHECKED', 'UNCHECKED']).nullable(), // EARLY_START only
});

export const SigningProofEmailSchema = z.object({
  kind: z.enum(SIGNING_EMAIL_KINDS),
  recipient: z.string(),
  subject: z.string(),
  bodyText: z.string(),
  attachmentFileName: z.string().nullable(),
  createdAt: z.iso.datetime(),
  sentAt: z.iso.datetime().nullable(),
  status: z.enum(['PENDING', 'SENT', 'FAILED']),
  deliveryStatus: z.enum(['DELIVERED', 'BOUNCED', 'COMPLAINED']).nullable(),
  deliveryStatusAt: z.iso.datetime().nullable(),
});

export const SigningProofSignerSchema = z.object({
  signerId: z.uuid(),
  contactId: refersTo('contact', z.uuid()),
  name: z.string(),
  email: z.string(),
  signedAt: z.iso.datetime().nullable(), // to the second; the UI shows Europe/Berlin
  typedName: z.string().nullable(),
  earlyStartRequested: z.boolean().nullable(),
  clientIp: z.string().nullable(),
  userAgent: z.string().nullable(),
  pageFingerprint: z.string().nullable(),
  acceptanceReply: z.object({ markedAt: z.iso.datetime(), markedByMemberId: refersTo('member', z.uuid()) }).nullable(), // null while outstanding, before signing and for a Vollmacht
  revocation: z
    .object({
      receivedAt: z.iso.datetime(),
      name: z.string(),
      contract: z.string(),
      email: z.string(),
      reason: z.string().nullable(),
      clientIp: z.string(),
      userAgent: z.string().nullable(),
    })
    .nullable(), // null unless this Eigentümer revoked the Maklervertrag
  declarations: z.array(SigningDeclarationSchema), // [] before a signature
  page: SigningPageReplaySchema.nullable(), // the snapshot content, for „Seite, wie angezeigt“
  emails: z.array(SigningProofEmailSchema), // oldest first
});

export const SigningProofSchema = z.object({
  id: z.uuid(),
  documentType: GeneratedDocumentTypeSchema,
  templateName: z.string(),
  propertyAddress: z.string(),
  sentByMemberId: refersTo('member', z.uuid()),
  sentAt: z.iso.datetime(),
  agentSignatureName: z.string(),
  companyName: z.string(),
  sentFingerprint: z.string(),
  signedFingerprint: z.string().nullable(),
  signers: z.array(SigningProofSignerSchema).min(1),
});

export const GetSigningProofResponseSchema = SigningProofSchema;
export type SigningDeclaration = z.infer<typeof SigningDeclarationSchema>;
export type SigningProof = z.infer<typeof SigningProofSchema>;
export type SigningProofSigner = z.infer<typeof SigningProofSignerSchema>;
export type SigningProofEmail = z.infer<typeof SigningProofEmailSchema>;
export type SigningPageReplay = z.infer<typeof SigningPageReplaySchema>;
